<!-- AdditionalProfileDetails.vue -->

<script setup>
import { ref, onMounted, computed } from "vue";
import { useUserStore } from "@/stores/useUserStore";
import { getUserById } from "@/services/userService";

const props = defineProps({
  isVisible: { type: Boolean, default: false },
});

const userStore = useUserStore();

const formData = ref({
  bio: "",
  marketingConsent: false,
});

const apiError = ref(null);
const isLoading = ref(false);

const originalUser = ref(null);

const currentUser = computed(() => userStore.currentUser);

const groups = computed(() => currentUser.value?.groups ?? []);

onMounted(async () => {
  try {
    if (!currentUser.value?.userId) {
      await userStore.refreshCurrentUser();
    }

    const userId = currentUser.value?.userId;
    if (!userId) {
      apiError.value = "Não foi possível carregar os dados do usuário.";
      return;
    }

    const response = await getUserById(userId);
    originalUser.value = response.data;

    formData.value.bio = originalUser.value.bio || "";
    formData.value.marketingConsent =
      originalUser.value.marketingConsent ?? false;
  } catch (error) {
    console.error("Erro ao carregar dados adicionais do usuário:", error);
    apiError.value =
      error.response?.data?.message ||
      error.message ||
      "Não foi possível carregar os dados adicionais.";
  }
});

function validateForm() {
  return true;
}

async function handleUpdate() {
  if (!validateForm()) return;
}

function getAdditionalPayload() {
  return {
    bio: formData.value.bio || "",
    marketingConsent: formData.value.marketingConsent,
  };
}

defineExpose({ getAdditionalPayload });
</script>

<template>
  <div class="form-container" v-show="props.isVisible">
    <h3 class="view-title">Demais dados</h3>
    <p class="view-subtitle">Ajuste informações complementares do seu perfil.</p>

    <form @submit.prevent="handleUpdate" id="additional-profile-form">
      <div class="form-group">
        <label for="bio">Biografia (opcional)</label>
        <textarea
          id="bio"
          v-model="formData.bio"
          class="form-control bio-textarea"
          rows="6"
          placeholder="Conte um pouco sobre você, seus interesses de leitura, áreas de estudo, etc."
        ></textarea>

        <p class="helper-text"></p>
      </div>

      <div class="form-group marketing-group">
        <label class="section-label">Marketing</label>

        <div class="marketing-consent-row">
          <input
            type="checkbox"
            id="marketingConsent"
            v-model="formData.marketingConsent"
            class="terms-checkbox"
          />

          <label for="marketingConsent" class="terms-label cursor-pointer">
            Concordo em receber comunicações de marketing, novidades e
            promoções do Litterae.
          </label>
        </div>
      </div>

      <div v-if="groups.length > 0" class="form-group mt-4">
        <label>Grupos</label>

        <div class="group-tags-container">
          <span
            v-for="group in groups"
            :key="group.groupId"
            class="group-tag"
          >
            {{ group.groupName }}
          </span>
        </div>
      </div>

      <div v-if="apiError" class="api-error-message mt-4">
        {{ apiError }}
      </div>
    </form>

    <div class="saving-hint" v-if="isLoading">Salvando alterações...</div>
  </div>
</template>

<style scoped>
.form-container {
  padding: 0.3rem;
  height: 100%;
  max-width: 72rem;
}

.view-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: var(--text-dark);
  margin: 0;
}

.view-subtitle {
  font-size: 0.9rem;
  color: var(--text-light);
  margin-top: 0.2rem;
  margin-bottom: 1.2rem;
}

.form-group label {
  margin-bottom: 0.35rem;
}

.form-control {
  width: 100%;
  padding: 0.55rem 0.6rem;
  border: 1px solid var(--border-color);
  background-color: var(--surface);
  color: var(--text-dark);
  font-size: 0.9rem;
  border-radius: 10px;
  transition: border-color 0.2s, box-shadow 0.2s;
}

.form-control:focus {
  outline: none;
  border-color: var(--focus-border);
  box-shadow: var(--focus-ring);
}

.bio-textarea {
  resize: vertical;
  min-height: 140px;
  line-height: 1.35;
}

.helper-text {
  margin-top: 0.35rem;
  font-size: 0.82rem;
  color: var(--text-light);
  line-height: 1.25;
}

.marketing-group {
  margin-top: 1.1rem;
}

.section-label {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-dark);
  margin-bottom: 0.5rem;
}

.marketing-consent-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.marketing-consent-row .terms-checkbox {
  margin: 0;
}

.marketing-consent-row .terms-label {
  margin-bottom: 0;
  display: flex;
  align-items: center;
}

.terms-checkbox {
  width: 1rem;
  height: 1rem;
  border-radius: 4px;
  flex-shrink: 0;
  cursor: pointer;
}

.terms-label {
  font-size: 0.9rem;
  font-weight: 700;
  color: var(--text-dark);
  cursor: pointer;
  line-height: 1.45;
  margin-bottom: 0;
}

.group-tags-container {
  display: flex;
  flex-wrap: wrap;
  gap: 0.45rem;
  padding: 0.45rem;
  background-color: var(--surface-2);
  border: 1px solid var(--border-color);
  border-radius: 10px;
}

.group-tag {
  background-color: var(--brand-soft);
  color: #4338ca;
  padding: 0.2rem 0.6rem;
  border-radius: 12px;
  font-size: 0.82rem;
  font-weight: 500;
}

.api-error-message {
  color: var(--text-important);
  font-size: 0.88rem;
  margin-top: 0.25rem;
  padding: 0.65rem 0.8rem;
  background-color: var(--danger-bg);
  border: 1px solid var(--danger-bg);
  border-radius: 10px;
  text-align: center;
}

.saving-hint {
  margin-top: 0.65rem;
  font-size: 0.85rem;
  color: var(--text-light);
}

@media (max-width: 420px) {
  .form-container {
    padding: 0.85rem;
  }
}
</style>